import React, { useContext, useEffect, useState } from "react";
import { Modal, Box, Typography, Switch } from "@mui/material";
import NeoButton from "./NeoButton";
import { useRoomsStore } from "../@shared/store/useRoomsStore";
import { useNavigate } from "react-router-dom";
import { Remove as RemoveIcon, Add as AddIcon } from "@mui/icons-material";
import { useAuthStore } from "../@shared/store/useAuthStore";
import { MainContext } from "../App";

const screenHeight = window.innerHeight;

const modalHeight = screenHeight * 0.6;
const modalWidth = modalHeight * (4 / 5);

const backgroundImage = require("../@shared/assets/game_menu.png");

const StartRoomModal = ({ visible, onClose }) => {
  const { userData } = useAuthStore();
  const { Theme, setScreenLoading } = useContext(MainContext);
  const [amountOfPlayers, setAmountOfPlayers] = useState(6);
  const [isRanked, setIsRanked] = useState(true);
  const [botFill, setBotFill] = useState(false);
  const [isPublic, setIsPublic] = useState(true);
  const [teams, setTeams] = useState(false);
  const [timedTurns, setTimedTurns] = useState(true);
  const [turnTime, setTurnTime] = useState(30);

  const [pve, setPve] = useState(false); // Defaults to false = Free-for-All

  const createAndSetRoom = useRoomsStore((s) => s.createAndSetRoom);
  const navigate = useNavigate();

  useEffect(() => {
    if (amountOfPlayers < 6 && isRanked) {
      setIsRanked(false);
    }
  }, [amountOfPlayers, isRanked]);

  const handleCreate = async () => {
    console.log("Currently Creating room with:", {
      amountOfPlayers,
      botFill,
      public: isPublic,
      teams,
      timedTurns,
      turnTime,
      ranked: isRanked,
      pve,
    });

    await createAndSetRoom(userData?.id, {
      amountOfPlayers,
      botFill,
      players: [
        {
          ...userData,
          ready: false,
          character: null,
          bot: false,
        },
      ],
      public: isPublic,
      ranked: isRanked,
      teams,
      timedTurns,
      turnTime,
      pve,
    });

    onClose();
    setScreenLoading(true);
    const timeout = setTimeout(() => {
      navigate("/lobby");
    }, 500); // small delay, but lets TransitionOverlay take over

    return () => clearTimeout(timeout);
  };

  // Reusable SettingToggle component
  const SettingToggle = ({ label, value, setValue, labelColor = "#000" }) => (
    <Box sx={styles.toggleRow}>
      <Typography sx={{ color: labelColor }}>{label}</Typography>
      <Switch checked={value} onChange={(e) => setValue(e.target.checked)} />
    </Box>
  );

  return (
    <Modal
      open={visible}
      onClose={onClose}
      sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <Box sx={styles.modalContainer}>
        <img
          src={backgroundImage}
          alt="Background"
          style={styles.backgroundImage}
        />
        <Box sx={styles.modalContent}>
          <Typography
            variant="h6"
            sx={{
              color: "#fff",
              fontWeight: "bold",
              mb: 2,
              textAlign: "center",
              padding: "10px 20px",
              borderRadius: "5px",
              backgroundColor: "#00000080",
            }}
          >
            Create Room
          </Typography>

          {/* Increment row for amountOfPlayers */}
          <Box
            style={{
              height: "calc(100% - 100px)",
              width: "96%",
              overflowY: "scroll",
              overflowX: "hidden",
              scrollBarWidth: "none",
            }}
          >
            <Box sx={styles.incrementRow}>
              <Box sx={styles.incrementButton}>
                <NeoButton
                  isIcon={true}
                  icon={<RemoveIcon sx={{ fontSize: 30, color: "#fff" }} />}
                  color={Theme.palette.secondary.main}
                  width={100}
                  onClick={() =>
                    setAmountOfPlayers((prev) => Math.max(2, prev - 1))
                  }
                />
              </Box>

              <Typography variant="h6" sx={{ color: "#fff", mx: 2 }}>
                {amountOfPlayers} Players
              </Typography>

              <Box sx={styles.incrementButton}>
                <NeoButton
                  isIcon={true}
                  icon={<AddIcon sx={{ fontSize: 30, color: "#fff" }} />}
                  color={Theme.palette.primary.main}
                  width={100}
                  onClick={() =>
                    setAmountOfPlayers((prev) => Math.min(12, prev + 1))
                  }
                />
              </Box>
            </Box>

            <SettingToggle
              label="Public Room"
              value={isPublic}
              setValue={setIsPublic}
              labelColor="#fff"
            />

            <SettingToggle
              label={isRanked ? "Ranked" : "Exhibition"}
              value={isRanked}
              setValue={setIsRanked}
              labelColor="#fff"
              disabled={amountOfPlayers < 6}
            />

            <SettingToggle
              label="Custom Timer"
              value={timedTurns}
              setValue={setTimedTurns}
              labelColor="#fff"
              disabled
            />

            {timedTurns && (
              <Box sx={{ mt: 2 }}>
                <Box sx={styles.incrementRow}>
                  <Box sx={styles.incrementButton}>
                    <NeoButton
                      isIcon={true}
                      icon={<RemoveIcon sx={{ fontSize: 30, color: "#fff" }} />}
                      color={Theme.palette.secondary.main}
                      width={100}
                      onClick={() =>
                        setTurnTime((prev) => Math.max(15, prev - 15))
                      }
                    />
                  </Box>

                  <Typography variant="h6" sx={{ color: "#fff", mx: 2 }}>
                    {turnTime} Seconds
                  </Typography>

                  <Box sx={styles.incrementButton}>
                    <NeoButton
                      isIcon={true}
                      icon={<AddIcon sx={{ fontSize: 30, color: "#fff" }} />}
                      color={Theme.palette.primary.main}
                      width={100}
                      onClick={() =>
                        setTurnTime((prev) => Math.min(60, prev + 15))
                      }
                    />
                  </Box>
                </Box>
              </Box>
            )}

            <SettingToggle
              label="Bot Fill"
              value={botFill}
              setValue={setBotFill}
              labelColor="#fff"
            />

            {/* COMMENT THIS BACK IN WHEN CO-OP IS UPDATED */}

            {botFill && !teams && (
              <SettingToggle
                label="Co-Op (PvE)"
                value={pve}
                setValue={() => {
                  setPve(!pve);
                  setTeams(false);
                }}
                labelColor="#fff"
              />
            )}

            {/*  {!pve && (
            <SettingToggle
              label="Enable Teams"
              value={teams}
              setValue={() => {
                setTeams(!teams);
                setPve(false);
              }}
              labelColor="#fff"
              disabled
            />
          )} */}

            <Box sx={styles.buttonRow}>
              <NeoButton
                text="Start"
                color={Theme.palette.primary.main}
                width={98}
                onClick={handleCreate}
              />
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            position: "absolute",
            bottom: "-20px",
            left: 0,
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <NeoButton
            text="Cancel"
            color={Theme.palette.secondary.main}
            width={49}
            onClick={onClose}
          />
        </Box>
      </Box>
    </Modal>
  );
};

const styles = {
  modalContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    p: 2,
    height: { xs: "70%", md: "60%" },
    width: { xs: "95%", md: "30%" },
    position: "absolute",
    outline: "none",
  },
  backgroundImage: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
  },
  modalContentWrapper: {
    width: modalWidth,
    height: modalHeight,
  },
  modalContent: {
    width: "100%",
    height: "100%",
    paddingTop: "18%",
    paddingLeft: "5%",
    paddingRight: "5%",
    paddingBottom: "12%",
    borderRadius: 16,
    elevation: 5,
    borderColor: "#fff",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  incrementRow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    mb: 2,
  },
  incrementButton: {
    width: 50,
  },
  toggleRow: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    my: 1,
    alignItems: "center",
  },
  buttonRow: {
    mt: 2,
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
  },
};

export default StartRoomModal;
